/**
 * Theme Hooks
 *
 * Custom hooks for accessing theme values in React components
 * NOTE: Prefer using AntD components directly over inline styles
 */

import { useMemo, useState, useEffect } from "react";
import { theme as antdTheme } from "antd";
import { Grid } from "antd";
import theme from "../../config/theme";

const { useBreakpoint: useAntdBreakpoint } = Grid;

/**
 * Hook to access AntD's design tokens from ConfigProvider
 * Use this to access theme values that respect the AntD theme system
 *
 * @returns AntD theme token object
 *
 * @example
 * const MyComponent = () => {
 *   const { token } = useAntdTheme();
 *   return <div style={{ color: token.colorPrimary }}>Hello</div>;
 * };
 */
export const useAntdTheme = () => {
  return antdTheme.useToken();
};

/**
 * DEPRECATED: Use AntD components with responsive props instead
 *
 * Hook to access the centralized theme configuration
 * @deprecated Use useAntdTheme() or AntD components directly
 *
 * @example
 * // OLD WAY (deprecated):
 * const theme = useTheme();
 * <div style={{ color: theme.colors.primary.main }}>Hello</div>
 *
 * // NEW WAY:
 * <Typography.Text type="primary">Hello</Typography.Text>
 */
export const useTheme = () => {
  console.warn("useTheme() is deprecated. Use useAntdTheme() or AntD components directly.");
  return useMemo(() => theme, []);
};

/**
 * Hook to get current active breakpoints
 * Uses AntD's built-in breakpoint detection
 *
 * @returns Object with breakpoint flags (xs, sm, md, lg, xl, xxl)
 *
 * @example
 * const MyComponent = () => {
 *   const screens = useResponsiveBreakpoints();
 *   const columns = screens.xs ? 1 : screens.sm ? 2 : screens.md ? 3 : 4;
 *   return <Row gutter={[16, 16]}>{items.map(...)}</Row>;
 * };
 */
export const useResponsiveBreakpoints = () => {
  return useAntdBreakpoint();
};

/**
 * Hook to get responsive values based on current screen size
 * Uses AntD's breakpoint system for accurate detection
 *
 * @param values - Object with breakpoint keys and values
 * @returns The appropriate value for current screen size
 *
 * @example
 * const MyComponent = () => {
 *   const columns = useResponsive({ xs: 1, sm: 2, md: 3, lg: 4 });
 *   // However, prefer using AntD's Col with responsive spans:
 *   // <Col xs={24} sm={12} md={8} lg={6}>
 * };
 */
export const useResponsive = <T>(values: {
  xs?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
  xxl?: T;
}): T | undefined => {
  const screens = useAntdBreakpoint();

  return useMemo(() => {
    // Check from largest to smallest
    if (screens.xxl && values.xxl !== undefined) return values.xxl;
    if (screens.xl && values.xl !== undefined) return values.xl;
    if (screens.lg && values.lg !== undefined) return values.lg;
    if (screens.md && values.md !== undefined) return values.md;
    if (screens.sm && values.sm !== undefined) return values.sm;
    if (screens.xs && values.xs !== undefined) return values.xs;

    // Fallback to smallest defined value
    return values.xs || values.sm || values.md || values.lg || values.xl || values.xxl;
  }, [screens, values]);
};

/**
 * DEPRECATED: Use AntD components instead of inline styles
 *
 * @deprecated Use AntD components with theme tokens
 *
 * @example
 * // OLD WAY (deprecated):
 * const styles = useThemedStyles((theme) => ({ container: { padding: theme.spacing.lg } }));
 *
 * // NEW WAY:
 * <Space direction="vertical" size="large">
 */
export const useThemedStyles = <T extends Record<string, React.CSSProperties>>(
  styleFactory: (appTheme: typeof theme) => T
): T => {
  console.warn(
    "useThemedStyles() is deprecated. Use AntD components (Space, Flex, etc.) instead of inline styles."
  );
  return useMemo(() => styleFactory(theme), [styleFactory]);
};

/**
 * Hook to check if currently on mobile screen
 * @returns true if screen is xs or sm
 */
export const useIsMobile = (): boolean => {
  const screens = useAntdBreakpoint();
  return useMemo(() => {
    return (screens.xs && !screens.md) || (screens.sm && !screens.md);
  }, [screens]);
};

/**
 * Hook to check if currently on tablet screen
 * @returns true if screen is md or lg (but not xl/xxl)
 */
export const useIsTablet = (): boolean => {
  const screens = useAntdBreakpoint();
  return useMemo(() => {
    return (screens.md || screens.lg) && !screens.xl;
  }, [screens]);
};

/**
 * Hook to check if currently on desktop screen
 * @returns true if screen is xl or xxl
 */
export const useIsDesktop = (): boolean => {
  const screens = useAntdBreakpoint();
  return useMemo(() => {
    return screens.xl === true || screens.xxl === true;
  }, [screens]);
};

export default {
  useAntdTheme,
  useTheme,
  useResponsive,
  useResponsiveBreakpoints,
  useThemedStyles,
  useIsMobile,
  useIsTablet,
  useIsDesktop,
};
